import axios from "axios";

/**
 * Authenticated file access via API.
 * Response shape:
 * { data: { url: "<sas-url>", expiresIn: 600 } }
 */
export function getSecureFileAccessUrl(fileId) {
  return `${process.env.REACT_APP_API_URL}/api/custom/files/${fileId}/access`;
}

const inflightSigned = new Map();
const inflightData = new Map();
/** @type {Map<string, { url: string, expiresAt: number }>} */
const signedCache = new Map();
/** @type {Map<string, { url: string, expiresAt: number }>} */
const dataCache = new Map();

function getCached(map, fileId) {
  const entry = map.get(String(fileId));
  if (!entry) return null;
  if (Date.now() >= entry.expiresAt - 15_000) {
    map.delete(String(fileId));
    return null;
  }
  return entry.url;
}

function setCached(map, fileId, url, expiresInSeconds = 600) {
  const ttlMs = Math.max(30, Number(expiresInSeconds) || 600) * 1000;
  map.set(String(fileId), {
    url,
    expiresAt: Date.now() + ttlMs,
  });
}

function arrayBufferToBase64(buffer) {
  return btoa(
    new Uint8Array(buffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      ""
    )
  );
}

async function requestSignedUrl(fileId, jwt) {
  const response = await axios.get(getSecureFileAccessUrl(fileId), {
    headers: { Authorization: `Bearer ${jwt}` },
  });

  const payload = response?.data?.data ?? response?.data;
  const url = payload?.url || payload?.signedUrl || null;
  const expiresIn = payload?.expiresIn ?? 600;

  if (!url) {
    throw new Error("Secure file access response missing data.url");
  }

  return { url, expiresIn };
}

/**
 * Returns the temporary signed URL from the access API (for normal <img src>).
 */
export async function fetchSecureFileObjectUrl(fileId, jwt) {
  if (!fileId || !jwt) {
    return null;
  }

  const key = String(fileId);
  const cached = getCached(signedCache, key);
  if (cached) {
    return cached;
  }
  if (inflightSigned.has(key)) {
    return inflightSigned.get(key);
  }

  const promise = (async () => {
    const { url, expiresIn } = await requestSignedUrl(fileId, jwt);
    setCached(signedCache, key, url, expiresIn);
    return url;
  })().finally(() => {
    inflightSigned.delete(key);
  });

  inflightSigned.set(key, promise);
  return promise;
}

/**
 * Access API → signed URL → download bytes → data URL.
 * Needed for html2canvas / Muat Turun (cross-origin Azure URLs are blank in canvas).
 */
export async function fetchSecureFileDataUrl(fileId, jwt) {
  if (!fileId || !jwt) {
    return null;
  }

  const key = String(fileId);
  const cached = getCached(dataCache, key);
  if (cached) {
    return cached;
  }
  if (inflightData.has(key)) {
    return inflightData.get(key);
  }

  const promise = (async () => {
    const { url, expiresIn } = await requestSignedUrl(fileId, jwt);
    setCached(signedCache, key, url, expiresIn);

    const fileResponse = await axios.get(url, {
      responseType: "arraybuffer",
    });
    const contentType =
      fileResponse.headers["content-type"] || "image/png";
    const dataUrl = `data:${contentType};base64,${arrayBufferToBase64(
      fileResponse.data
    )}`;

    setCached(dataCache, key, dataUrl, expiresIn);
    return dataUrl;
  })().finally(() => {
    inflightData.delete(key);
  });

  inflightData.set(key, promise);
  return promise;
}

export function revokeSecureFileUrl(url) {
  if (url && typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

export function resolveMediaFileId(...media) {
  for (const item of media) {
    const id = item?.id ?? item;
    if (id != null && id !== "" && typeof id !== "object") {
      return id;
    }
  }
  return null;
}
